import template from "./string-multi.html";
import "./string-multi.less";
import {
	EVENT_CLEAR_VALUE,
	EVENT_REVERT_VALUES,
	EVENT_REPORT_LOCKED,
	EVENT_REPORT_UNLOCKED,
	EVENT_SET_FORM_PRISTINE,
	EVENT_UNCLEAR_VALUE,
	EVENT_VALUE_CHANGED
} from "constants.js";
import {
	areStringValuesEqual,
	beginEditingStringValue,
	getAllowedStringValues,
	getStoredStringValues,
	insertStringValuesAt,
	isCharacterAllowed,
	isNewEntryCharacter,
	isValueAllowed,
	limitStringValues,
	splitNewEntryValue,
	toStringFieldValues
} from "./string-multi-values";

export class Controller {
	items = [];
	value = "";
	editingItem = undefined;
	editingIndex = undefined;

	constructor($scope, eventBus, $element, $timeout, $logger) {
		this._scope = $scope;
		this._element = $element;
		this.$timeout = $timeout;
		this.$logger = $logger;
	}

	$onInit() {
		this._eventBus = this.reportScope.events;
		this.initialValues = this.getStoredValues();
		this.items = [...this.initialValues];
		this._unregister = this.reportFormCtrl.registerFieldController(this);

		this._eventBus.register(EVENT_CLEAR_VALUE(this.field.Id), (e, params = {}) => {
			if(!this.cleared && params.isInitializing !== true && (this.items.length > 0 || this.value)) {
				this.cleared = {
					items: [...this.items],
					value: this.value,
					editingItem: this.editingItem,
					editingIndex: this.editingIndex
				};
			}

			this.items = [];
			this.value = "";
			this.editingItem = undefined;
			this.editingIndex = undefined;
			this.handleValueChanged();
		}, this._scope);

		this._eventBus.register(EVENT_UNCLEAR_VALUE(this.field.Id), () => {
			if(this.cleared) {
				this.items = [...this.cleared.items];
				this.value = this.cleared.value;
				this.editingItem = this.cleared.editingItem;
				this.editingIndex = this.cleared.editingIndex;
				this.cleared = undefined;
				this.handleValueChanged();
			}
		}, this._scope);

		this._eventBus.register(EVENT_REVERT_VALUES, () => {
			this.revertValue();
		}, this._scope);

		this._eventBus.register(EVENT_REPORT_LOCKED, () => {
			this.field.isReadonly = true;
		}, this._scope);

		this._eventBus.register(EVENT_REPORT_UNLOCKED, () => {
			this.field.isReadonly = false;
		}, this._scope);

		this._eventBus.register(EVENT_SET_FORM_PRISTINE, () => {
			this.setPristine();
		}, this._scope);
	}

	$onDestroy() {
		this._unregister();
	}

	getCurrentValue(isApiCall = false) {
		const result = this.buildResult();

		if(!isApiCall && this.field.IsNotReported !== true && this.field.IsHidden !== true && this.isDirty()) {
			result.forEach(fieldValue => fieldValue.isDirty = true);
		}

		return result;
	}

	buildResult() {
		const result = toStringFieldValues(this.field.Id, this.items);

		return result;
	}

	getStoredValues() {
		return getStoredStringValues(this.field.Id, this.items);
	}

	revertValue() {
		this.items = [...this.initialValues];
		this.value = "";
		this.editingItem = undefined;
		this.editingIndex = undefined;
		this.handleValueChanged();
	}

	setPristine() {
		this.initialValues = [...this.items];
		this.checkForDirty();
	}

	handleInput() {
		const splitValue = splitNewEntryValue(this.value, this.field.NewEntryRegex);

		if(splitValue.hasNewEntry) {
			this.commitValues(splitValue.values, { focusEnd: this.isEditing() });
			this.value = "";
			return;
		}

		this.value = this.getTypedValue(this.value);

		this.checkForDirty();
	}

	handleKeydown(event) {
		if(this.shouldIgnoreKeydown(event)) {
			return;
		}

		if(event.key === "Enter") {
			event.preventDefault();
			this.commitValue({ focusEnd: this.isEditing() });
		}
		else if(event.key === "Escape" && this.isEditing()) {
			event.preventDefault();
			this.cancelEdit();
		}
		else if(event.key.length === 1 && this.isNewEntryKey(event.key)) {
			event.preventDefault();

			if(!this.hasMaxValueLengthReached() && this.hasPendingValue()) {
				this.commitValue();
			}
		}
		else if(event.key.length === 1 && !this.canAcceptCharacter(event.key)) {
			event.preventDefault();
		}
	}

	handlePaste(event) {
		const pastedText = event.originalEvent?.clipboardData?.getData("text")
			?? event.clipboardData?.getData("text");

		if(pastedText === undefined) {
			return;
		}

		event.preventDefault();

		const target = event.target;
		const selectionStart = target.selectionStart ?? this.value.length;
		const selectionEnd = target.selectionEnd ?? selectionStart;
		const pastedValue = `${this.value.slice(0, selectionStart)}${pastedText}${this.value.slice(selectionEnd)}`;
		const splitValue = splitNewEntryValue(pastedValue, this.field.NewEntryRegex);

		if(splitValue.hasNewEntry) {
			this.commitValues(splitValue.values, { focusEnd: this.isEditing() });
			return;
		}

		this.value = isValueAllowed(pastedValue, this.getValidationOptions())
			? pastedValue
			: "";

		this.checkForDirty();
	}

	handleTagKeydown(event, index) {
		if(event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
			event.preventDefault();
			this.editItem(index);
		}
	}

	handleFieldClick(event) {
		if(this.isReadonly() || !this.shouldShowEntryBlock()) {
			return;
		}

		if(this.hasAncestorWithClass(event.target, "choices__item", event.currentTarget)
			|| this.hasAncestorWithClass(event.target, "string-multi-add", event.currentTarget)
			|| this.hasAncestorWithClass(event.target, "string-multi-edit-action", event.currentTarget)) {
			return;
		}

		this.focusInput("start");
	}

	commitValue(options) {
		this.commitValues([this.value], options);
	}

	commitValues(values, options = {}) {
		const normalizedValues = this.getValuesToCommit(values);

		if(normalizedValues.length === 0) {
			this.checkForDirty();
			return false;
		}

		this.items = this.isEditing()
			? insertStringValuesAt(this.items, normalizedValues, this.editingIndex)
			: this.items.concat(normalizedValues);

		this.value = "";
		this.editingItem = undefined;
		this.editingIndex = undefined;
		this.handleValueChanged();

		if(options.focusEnd) {
			this.focusInput();
		}

		return true;
	}

	editItem(index) {
		if(this.isReadonly() || this.isEditing() || this.hasPendingValue()) {
			return;
		}

		const editState = beginEditingStringValue(this.items, index);

		this.items = editState.items;
		this.value = editState.value;
		this.editingItem = editState.value;
		this.editingIndex = editState.editingIndex;
		this.handleValueChanged();
		this.focusInput();
	}

	removeItem(index, event) {
		event?.stopPropagation();
		this.items = this.items.filter((_, itemIndex) => itemIndex !== index);

		if(this.isEditing() && index < this.editingIndex) {
			this.editingIndex = this.editingIndex - 1;
		}

		this.handleValueChanged();
	}

	removeEditingItem(event) {
		event?.stopPropagation();

		this.value = "";
		this.editingItem = undefined;
		this.editingIndex = undefined;
		this.handleValueChanged();
		this.focusInput();
	}

	cancelEdit(event) {
		event?.stopPropagation();

		if(this.isEditing()) {
			this.items = insertStringValuesAt(this.items, [this.editingItem], this.editingIndex);
		}

		this.value = "";
		this.editingItem = undefined;
		this.editingIndex = undefined;
		this.handleValueChanged();
	}

	checkForDirty() {
		const isDirty = (this.field.visible !== false || this.initialValues.length > 0) && this.isDirty();
		this.setFieldDirty(this.field.Id, isDirty);
	}

	handleValueChanged() {
		this.checkForDirty();
		this._eventBus.trigger(EVENT_VALUE_CHANGED(this.field.Id, this.repeatId), {
			field: this.field,
			value: [...this.items]
		});
	}

	isDirty() {
		return this.field.IsNotReported !== true
			&& this.field.IsHidden !== true
			&& !areStringValuesEqual(this.items, this.initialValues);
	}

	isEditing() {
		return this.editingIndex !== undefined;
	}

	isReadonly() {
		return this.field.isReadonly || this.field.isDisabled;
	}

	hasPendingValue() {
		return (this.value ?? "").trim().length > 0;
	}

	canCommitCurrentValue() {
		return this.hasPendingValue()
			&& this.hasAvailableValueSlot()
			&& isValueAllowed(this.value, this.getValidationOptions());
	}

	getValidationOptions() {
		return {
			allowedCharacterRegex: this.field.AllowedCharacterRegex,
			maxValueLength: this.field.MaxValueLength
		};
	}

	getValidationStyling() {
		return this.field.hasValidationError ? "validation-error"
			: this.field.hasValidationWarning ? "validation-warning" : "";
	}

	getItemNames(separator = ", ") {
		return this.items.join(separator);
	}

	shouldShowEntryBlock() {
		return this.isEditing() || this.hasAvailableValueSlot();
	}

	shouldShowEditorAfterTags() {
		return this.shouldShowEntryBlock()
			&& (!this.isEditing() || this.editingIndex === this.items.length)
			&& this.field.isReadonly !== true;
	}

	shouldIgnoreKeydown(event) {
		return event.ctrlKey || event.metaKey || event.altKey;
	}

	isNewEntryKey(character) {
		return isNewEntryCharacter(character, this.field.NewEntryRegex);
	}

	canAcceptCharacter(character) {
		return this.hasAvailableValueSlot()
			&& !this.hasMaxValueLengthReached()
			&& isCharacterAllowed(character, this.field.AllowedCharacterRegex);
	}

	hasMaxValueLengthReached() {
		const maxValueLength = this.field.MaxValueLength;

		return maxValueLength !== undefined && (this.value ?? "").trim().length >= maxValueLength;
	}

	hasAvailableValueSlot() {
		const maxValues = this.field.MaxValues;

		return maxValues === undefined || this.items.length < maxValues;
	}

	getValuesToCommit(values) {
		const allowedValues = getAllowedStringValues(values, this.getValidationOptions());
		const availableSlots = this.field.MaxValues === undefined ? undefined : this.field.MaxValues - this.items.length;

		return limitStringValues(allowedValues, availableSlots);
	}

	getTypedValue(value) {
		const allowedCharacters = (value ?? "")
			.split("")
			.filter(character => isCharacterAllowed(character, this.field.AllowedCharacterRegex))
			.join("");

		const maxValueLength = this.field.MaxValueLength;

		return maxValueLength === undefined
			? allowedCharacters
			: allowedCharacters.slice(0, maxValueLength);
	}

	hasAncestorWithClass(target, className, stopNode) {
		let currentNode = target;

		while(currentNode && currentNode !== stopNode) {
			if(currentNode.classList?.contains(className)) {
				return true;
			}

			currentNode = currentNode.parentElement;
		}

		return false;
	}

	focusInput(cursorPosition = "end") {
		this.$timeout(() => {
			const input = this._element[0].querySelector(".string-multi-input:not([disabled])");

			if(!input) {
				return;
			}

			input.focus();

			const selectionIndex = cursorPosition === "start"
				? 0
				: input.value.length;

			input.setSelectionRange(selectionIndex, selectionIndex);
		});
	}
}

export default angular
	.module("vesl.components.report-form.string-multi", [])
	.component("stringMulti", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			repeatId: "<",
			values: "<",
			setFieldDirty: "="
		},
		require: {
			reportFormCtrl: "^reportForm",
			reportScope: "^^reportScope"
		}
	}).name;